import { Field as FormikField, FieldProps } from 'formik';
import * as React from 'react';
import { uid } from 'react-uid';
import { TextField } from '../../../../components/TextField';
import { Field } from '../../../Field';
import { List, ListItem } from '../../../List';
import { FormField } from '../../types';
import { getNestedValue } from '../../utils';

const hasError = (id: string, touched: Record<string, unknown>, errors: Record<string, unknown>) =>
  touched && errors && !!getNestedValue(id, touched) && !!getNestedValue(id, errors);

const getHelperText = (
  id: string,
  touched: Record<string, unknown>,
  errors: Record<string, unknown>,
  helperText: React.ReactNode,
): string | undefined =>
  (hasError(id, touched, errors) ? getNestedValue(id, errors) : helperText) as string | undefined;

export interface FormEditableProps {
  fields: FormField[];
  errors: Record<string, unknown>;
  touched: Record<string, unknown>;
  disabled: boolean;
  inset?: boolean;
}

export const FormEditable: React.FC<FormEditableProps> = ({ fields, errors, touched, disabled, inset }) => (
  <List inset={inset}>
    {fields.map((field, idx) => {
      const { id, label, inputComponent: Input, inputProps = {} } = field;

      const {
        InputProps,
        helperText: inputPropsHelperText,
        inputProps: inputElementProps,
        name: inputPropsName,
        onBlur: inputPropsOnBlur,
        onChange: inputPropsOnChange,
        onFocus,
        type,
        value: inputPropsValue,
      } = inputProps;

      const error = hasError(id, touched, errors);
      const helperText = getHelperText(id, touched, errors, inputPropsHelperText);

      return (
        <ListItem key={uid(field, idx)}>
          <Field label={label}>
            <FormikField name={id}>
              {({ field: formikField }: FieldProps<unknown>) => {
                const {
                  checked,
                  multiple,
                  name: formikFieldName,
                  onBlur: formikFieldOnBlur,
                  onChange: formikFieldOnChange,
                  value: formikFieldValue,
                } = formikField;

                const name = inputPropsName ?? formikFieldName ?? '';
                const onBlur = inputPropsOnBlur ?? formikFieldOnBlur;
                const onChange = inputPropsOnChange ?? formikFieldOnChange;
                const value = inputPropsValue ?? formikFieldValue;

                if (Input) {
                  return (
                    <Input
                      checked={checked}
                      disabled={disabled}
                      error={error}
                      helperText={helperText}
                      multiple={multiple}
                      name={name}
                      onBlur={onBlur}
                      onChange={onChange}
                      value={value}
                    />
                  );
                }

                return (
                  <TextField
                    InputProps={InputProps}
                    disabled={disabled}
                    error={error}
                    fullWidth
                    helperText={helperText}
                    inputElementProps={inputElementProps}
                    name={name}
                    onBlur={onBlur}
                    onChange={onChange}
                    onFocus={onFocus}
                    type={type}
                    value={value}
                  />
                );
              }}
            </FormikField>
          </Field>
        </ListItem>
      );
    })}
  </List>
);
